import { supabase } from '../config/supabase';
import { roomService, getLocationFromIp } from './roomService';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.currentRoom = null;
    this.currentChannel = null;
    
    this.deviceId = localStorage.getItem('clipshare-device-id') || `User-${Math.floor(Math.random() * 9000) + 1000}`;
    this.deviceIp = 'Detecting...';
    
    this.clipboardItems = new Map();
    this.chatMessages = new Map();
    this.connectedDevices = new Map();
    this.globalChannel = null;

    if (!localStorage.getItem('clipshare-device-id')) {
      localStorage.setItem('clipshare-device-id', this.deviceId);
    }

    this.fetchIpAddress();
    this.setupGlobalChannel();
  }

  async fetchIpAddress() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      this.deviceIp = data.ip;
      if (this.currentChannel) this.broadcastPresence();
    } catch (error) {
      this.deviceIp = 'Unknown';
    }
  }

  setupGlobalChannel() {
    this.globalChannel = supabase.channel('site-wide-activity', {
      config: { broadcast: { self: true } }
    });
    this.globalChannel
      .on('broadcast', { event: 'site_activity' }, (payload) => {
        if (payload.payload.senderId !== this.deviceId) {
          this.emit('site_activity', payload.payload);
        }
      })
      .subscribe();
  }

  getGlobalChannel() {
    return {
      on: (event, handler) => this.on(event, handler),
      off: (event, handler) => this.off(event, handler)
    };
  }

  broadcastActivity(type, location) {
    if (this.globalChannel) {
      this.globalChannel.send({
        type: 'broadcast',
        event: 'site_activity',
        payload: { type, location: location || 'Somewhere', senderId: this.deviceId }
      });
    }
  }

  on(event, handler) {
    if (!this.listeners.has(event)) this.listeners.set(event, []);
    this.listeners.get(event).push(handler);
  }

  off(event, handler) {
    const handlers = this.listeners.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) handlers.splice(index, 1);
    }
  }

  emit(event, data) {
    const handlers = this.listeners.get(event);
    if (handlers) handlers.forEach(h => h(data));
  }

  connect() {
    if (this.socket) return this.socket;
    
    this.socket = {
      emit: (event, data, callback) => {
        if (event === 'join_room') {
          this.currentRoom = data.room;
          this.setupRealtimeSubscription();
        } else if (event === 'add_clipboard_item') {
          this.addClipboardItem(data.room, data.item);
          getLocationFromIp(this.deviceIp).then(loc => this.broadcastActivity('share', loc));
        } else if (event === 'send_chat_message') {
          this.sendChatMessage(data.room, data.message);
        } else if (event === 'add_item_comment') {
          this.addItemComment(data.room, data.itemId, data.comment);
        } else if (event === 'destroy_room') {
          this.destroyRoomInternal(data.room);
        }
        if (callback) callback();
      },
      on: (event, handler) => this.on(event, handler),
      off: (event, handler) => this.off(event, handler),
      disconnect: () => this.disconnect()
    };

    setTimeout(() => {
      this.isConnected = true;
      this.emit('connect');
    }, 100);

    return this.socket;
  }

  broadcastPresence() {
    if (this.currentChannel) {
      this.currentChannel.send({
        type: 'broadcast',
        event: 'user_presence',
        payload: { deviceId: this.deviceId, ip: this.deviceIp, timestamp: new Date().toISOString() }
      });
    }
  }

  async setupRealtimeSubscription() {
    if (!this.currentRoom) return;

    const channelName = `room-${this.currentRoom}`;
    if (this.currentChannel) this.currentChannel.unsubscribe();

    this.currentChannel = supabase.channel(channelName, {
      config: { broadcast: { self: true } }
    });

    this.currentChannel
      .on('broadcast', { event: 'clipboard_item' }, (payload) => {
        if (payload.payload && payload.payload.item) {
          this.handleReceivedClipboardItem(payload.payload.item);
        }
      })
      .on('broadcast', { event: 'chat_message' }, (payload) => {
        if (payload.payload && payload.payload.message) {
          this.handleReceivedChatMessage(payload.payload.message);
        }
      })
      .on('broadcast', { event: 'item_comment' }, (payload) => {
        if (payload.payload && payload.payload.itemId) {
          this.handleReceivedItemComment(payload.payload.itemId, payload.payload.comment);
        }
      })
      .on('broadcast', { event: 'request_sync' }, (payload) => {
        if (payload.payload.senderId !== this.deviceId) {
          this.broadcastPresence();
          const items = this.clipboardItems.get(this.currentRoom) || [];
          const chats = this.chatMessages.get(this.currentRoom) || [];
          this.currentChannel.send({
            type: 'broadcast',
            event: 'clipboard_sync',
            payload: { items, chats, senderId: this.deviceId }
          });
        }
      })
      .on('broadcast', { event: 'clipboard_sync' }, (payload) => {
        if (payload.payload) {
          if (payload.payload.items) {
            const currentItems = this.clipboardItems.get(this.currentRoom) || [];
            const mergedItems = [...payload.payload.items];
            currentItems.forEach(item => {
              if (!mergedItems.some(m => m.id === item.id)) mergedItems.push(item);
            });
            mergedItems.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            this.clipboardItems.set(this.currentRoom, mergedItems);
            this.emit('clipboard_sync', mergedItems);
          }
          if (payload.payload.chats) {
            const currentChats = this.chatMessages.get(this.currentRoom) || [];
            const mergedChats = [...payload.payload.chats];
            currentChats.forEach(chat => {
              if (!mergedChats.some(m => m.id === chat.id)) mergedChats.push(chat);
            });
            mergedChats.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            this.chatMessages.set(this.currentRoom, mergedChats);
            this.emit('chat_sync', mergedChats);
          }
        }
      })
      .on('broadcast', { event: 'user_presence' }, (payload) => {
        if (payload.payload) {
          this.connectedDevices.set(payload.payload.deviceId, {
            ...payload.payload,
            isCurrent: payload.payload.deviceId === this.deviceId
          });
          this.emitConnectedDevices();
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          this.broadcastPresence();
          this.currentChannel.send({
            type: 'broadcast',
            event: 'request_sync',
            payload: { senderId: this.deviceId }
          });
          this.emitConnectedDevices();
        }
      });
  }

  sendChatMessage(roomName, content) {
    const newMsg = {
      id: `chat-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      senderId: this.deviceId,
      content,
      timestamp: new Date().toISOString()
    };
    
    // Add locally immediately
    if (!this.chatMessages.has(roomName)) this.chatMessages.set(roomName, []);
    const roomChats = this.chatMessages.get(roomName);
    roomChats.push(newMsg);
    
    // Emit to local listeners
    this.emit('new_chat_message', newMsg);
    
    // Broadcast to others
    if (this.currentChannel) {
      this.currentChannel.send({ 
        type: 'broadcast', 
        event: 'chat_message', 
        payload: { message: newMsg } 
      });
    }
  }

  handleReceivedChatMessage(message) {
    if (!this.chatMessages.has(this.currentRoom)) {
      this.chatMessages.set(this.currentRoom, []);
    }
    const roomChats = this.chatMessages.get(this.currentRoom);
    if (!roomChats.some(ex => ex.id === message.id)) {
      roomChats.push(message);
      this.emit('new_chat_message', message);
    }
  }

  addItemComment(roomName, itemId, commentText) {
    const newComment = {
      id: `comment-${Date.now()}`,
      senderId: this.deviceId,
      content: commentText,
      timestamp: new Date().toISOString()
    };
    this.handleReceivedItemComment(itemId, newComment);
    if (this.currentChannel) {
      this.currentChannel.send({ type: 'broadcast', event: 'item_comment', payload: { itemId, comment: newComment } });
    }
  }

  handleReceivedItemComment(itemId, comment) {
    const roomItems = this.clipboardItems.get(this.currentRoom) || [];
    const item = roomItems.find(i => i.id === itemId);
    if (item) {
      if (!item.comments) item.comments = [];
      if (!item.comments.some(c => c.id === comment.id)) {
        item.comments.push(comment);
        this.emit('item_updated', item);
      }
    }
  }

  handleReceivedClipboardItem(item) {
    if (!item || item.room_name !== this.currentRoom) return;
    if (!this.clipboardItems.has(this.currentRoom)) this.clipboardItems.set(this.currentRoom, []);
    const roomItems = this.clipboardItems.get(this.currentRoom);
    if (!roomItems.some(ex => ex.id === item.id)) {
      roomItems.unshift(item);
      this.emit('new_clipboard_item', item);
    }
  }

  async addClipboardItem(roomName, item) {
    const newItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`,
      room_name: roomName,
      type: item.type || 'text',
      content: item.content,
      fileName: item.fileName,
      fileSize: item.fileSize,
      fileType: item.fileType,
      device_name: item.deviceName || this.deviceId,
      timestamp: item.timestamp || new Date().toISOString(),
      comments: []
    };
    if (!this.clipboardItems.has(roomName)) this.clipboardItems.set(roomName, []);
    this.clipboardItems.get(roomName).unshift(newItem);
    this.emit('new_clipboard_item', newItem);
    if (this.currentChannel) {
      this.currentChannel.send({ type: 'broadcast', event: 'clipboard_item', payload: { item: newItem } });
    }
    return newItem;
  }

  destroyRoomInternal(roomName) {
    if (this.currentChannel) {
      this.currentChannel.send({ type: 'broadcast', event: 'room_destroyed', payload: { room: roomName, destroyedBy: this.deviceId } });
    }
  }

  emitConnectedDevices() {
    this.emit('connected_devices', Array.from(this.connectedDevices.values()));
    this.emit('user_count_update', { count: Math.max(1, this.connectedDevices.size) });
  }

  disconnect() {
    this.isConnected = false;
    if (this.currentChannel) {
      this.currentChannel.unsubscribe();
      this.currentChannel = null;
    }
    this.socket = null;
    this.currentRoom = null;
    this.connectedDevices.clear();
    this.emit('disconnect');
  }

  getSocket() { return this.socket; }
  getDeviceId() { return this.deviceId; }
}

export const socketService = new SocketService();